/**
 * "응답이 왔을 때 입력이 그대로인가" 지문 — 이름 붙은 필드 목록으로 선언한다.
 *
 * 위치 기반 배열(`JSON.stringify([a, b, c])`)은 무엇이 비교되는지 읽히지 않아 필드 누락이
 * 리뷰에서 늦게 잡혔다(문제 이미지·해설·난이도·builderContext). 필드 이름이 곧 비교 대상
 * 목록이다. 지문은 한 요청의 요청 시점·적용 시점 사이에서만 비교되므로 모양이 바뀌어도
 * 저장·호환 영향은 없다.
 */
import { type BuildQuizListItem } from '../../../form/schema/BuildQuizListSchema';
import { type BuilderContextInput } from '../types/questionCardAi.types';

import { toSourceQuestion } from './toSourceQuestion';

export type RevisionOf = (
  quiz: BuildQuizListItem,
  builderContext: BuilderContextInput,
) => string;

type RevisionField = (
  quiz: BuildQuizListItem,
  builderContext: BuilderContextInput,
) => unknown;

export const defineRevision =
  (fields: Record<string, RevisionField>): RevisionOf =>
  (quiz, builderContext) =>
    JSON.stringify(
      Object.fromEntries(
        Object.entries(fields).map(([name, read]) => [
          name,
          read(quiz, builderContext),
        ]),
      ),
    );

/** 액션들이 골라 쓰는 지문 필드. */
export const REVISION_FIELDS = {
  question: quiz => quiz.question,
  questionImage: quiz => quiz.questionImageUrl ?? '',
  builderContext: (_, builderContext) => builderContext,
  /** 실제 요청 본문에 실리는 문항 전체. */
  source: quiz => toSourceQuestion(quiz),
  /** 서버가 맥락으로 보는 해설·난이도·성취기준·힌트(요청 본문에 실린다). */
  sourceContext: quiz => {
    const { comment, difficulty, curriculum_list, hint } =
      toSourceQuestion(quiz);
    return {
      comment: comment ?? '',
      difficulty: difficulty ?? null,
      curriculum: curriculum_list ?? [],
      hint: hint ?? null,
    };
  },
} satisfies Record<string, RevisionField>;

/**
 * 유사 문제용 지문 — **실제 요청에 실린 입력 전체**.
 * 채움과 달리 부분 편집을 허용할 이유가 없다. 정답만 비교하면 ox 정답·추가 인정
 * 답안·힌트·난이도·과목(builderContext) 변경이 전부 통과해, 바뀐 원본 아래에
 * 옛 입력으로 만든 문항이 삽입된다. 문제 이미지는 요청 본문에 없지만 자격(이미지 문항
 * 차단)을 바꾸므로 넣는다.
 */
export const similarRevision = defineRevision({
  source: REVISION_FIELDS.source,
  builderContext: REVISION_FIELDS.builderContext,
  questionImage: REVISION_FIELDS.questionImage,
});
